import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ImagePlus, Loader2, ShieldCheck, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../ui/dialog";
import { createCollection } from "../../../lib/investAdminApi";
import { CATEGORIES, CATEGORY_LABEL, fmtNum, fmtUsd } from "./bcShared";

const EMPTY = { name: "", symbol: "", category: "SUV", pricePerTokenUsd: "", initialSupply: "1000", description: "" };
const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

// Mirrors chain-service validation so errors show inline before anything hits the chain.
function validate(f, image) {
  const e = {};
  if (f.name.trim().length < 2 || f.name.trim().length > 60) e.name = "2–60 characters.";
  if (!/^[A-Z0-9]{2,10}$/.test(f.symbol)) e.symbol = "2–10 letters or digits.";
  if (!CATEGORIES.includes(f.category)) e.category = "Pick a category.";
  if (!/^\d+(\.\d{1,2})?$/.test(f.pricePerTokenUsd) || Number(f.pricePerTokenUsd) <= 0) e.pricePerTokenUsd = "A positive amount, max 2 decimals.";
  const supply = Number(f.initialSupply);
  if (!Number.isInteger(supply) || supply < 1 || supply > 1_000_000_000) e.initialSupply = "Whole number from 1 to 1,000,000,000.";
  if (f.description.length > 500) e.description = "500 characters max.";
  if (!image) e.image = "A cover image is required.";
  return e;
}

function Field({ id, label, hint, error, children }) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-semibold mb-1.5">{label}</label>
      {children}
      {error ? (
        <p id={`${id}-err`} className="text-2xs mt-1 text-[var(--zc-danger)]" role="alert">{error}</p>
      ) : hint ? (
        <p className="text-2xs mt-1 text-[var(--zc-text-3)]">{hint}</p>
      ) : null}
    </div>
  );
}

function PreviewCard({ form, imageUrl }) {
  return (
    <div className="zc-card overflow-hidden" aria-label="Investor preview">
      <div className="aspect-[4/3] bg-[var(--zc-bg-3)] grid place-items-center overflow-hidden">
        {imageUrl
          ? <img src={imageUrl} alt="" className="w-full h-full object-cover" />
          : <ImagePlus size={22} className="text-[var(--zc-text-3)]" />}
      </div>
      <div className="p-4">
        <div className="eyebrow text-[var(--zc-secondary)]">{CATEGORY_LABEL[form.category]} token</div>
        <div className="font-semibold mt-1 truncate">{form.name || "Collection name"}</div>
        <div className="text-2xs font-mono text-[var(--zc-text-3)] mt-0.5">{form.symbol || "SYMBOL"}</div>
        <div className="flex items-end justify-between mt-3">
          <div>
            <div className="zc-stat text-xl">{form.pricePerTokenUsd ? fmtUsd(Math.round(Number(form.pricePerTokenUsd) * 100)) : "$—"}</div>
            <div className="text-2xs text-[var(--zc-text-3)]">per token</div>
          </div>
          <div className="text-right text-2xs text-[var(--zc-text-3)]">
            <b className="text-[var(--zc-text)] tabular-nums">{fmtNum(Number(form.initialSupply) || 0)}</b> available
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CreateCollectionDialog({ open, onOpenChange, paused, existingSymbols, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [image, setImage] = useState(null);
  const [touched, setTouched] = useState({});
  const [step, setStep] = useState("edit");
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const imageUrl = useMemo(() => (image ? URL.createObjectURL(image) : null), [image]);
  useEffect(() => () => imageUrl && URL.revokeObjectURL(imageUrl), [imageUrl]);

  useEffect(() => {
    if (open) { setForm(EMPTY); setImage(null); setTouched({}); setStep("edit"); }
  }, [open]);

  const errors = useMemo(() => {
    const e = validate(form, image);
    if (!e.symbol && existingSymbols.includes(form.symbol)) e.symbol = "Another collection already uses this symbol.";
    return e;
  }, [form, image, existingSymbols]);
  const showErr = (k) => (touched[k] || touched._submit ? errors[k] : null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "symbol" ? e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") : e.target.value }));
  const blur = (k) => () => setTouched((t) => ({ ...t, [k]: true }));

  const pickImage = (file) => {
    if (!file) return;
    if (!IMAGE_TYPES.includes(file.type)) return toast.error("Use a PNG, JPEG or WEBP image");
    if (file.size > MAX_IMAGE_BYTES) return toast.error("Image must be 5 MB or smaller");
    setImage(file);
    setTouched((t) => ({ ...t, image: true }));
  };

  const review = () => {
    setTouched((t) => ({ ...t, _submit: true }));
    if (Object.keys(errors).length === 0) setStep("review");
  };

  const submit = async () => {
    setBusy(true);
    try {
      const created = await createCollection(form, image);
      toast.success(`${created.name} minted — token #${created.tokenId}`);
      onOpenChange(false);
      onCreated();
    } catch (e) {
      toast.error(e.message || "Mint failed");
      setStep("edit");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = (k) => "zc-input w-full h-10 px-3" + (showErr(k) ? " !border-[var(--zc-danger)]" : "");

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="bc-create-dialog">
        <DialogHeader>
          <DialogTitle>{step === "edit" ? "New token collection" : "Review and mint"}</DialogTitle>
          <DialogDescription>
            {step === "edit"
              ? "Creates an ERC-1155 token on the contract and mints the initial supply to the admin wallet, ready for sale."
              : "Minting writes to the blockchain. Name, symbol and price can't be changed afterwards — only supply can grow."}
          </DialogDescription>
        </DialogHeader>

        {paused && (
          <div className="text-xs p-3 rounded-lg bg-[var(--zc-accent-soft)] text-[var(--zc-accent)]" role="alert">
            The contract is paused — minting will fail until you resume it from Overview.
          </div>
        )}

        {step === "edit" ? (
          <div className="grid md:grid-cols-[1fr,240px] gap-6">
            <div className="space-y-4">
              <Field id="bc-name" label="Collection name" error={showErr("name")}>
                <input id="bc-name" className={inputCls("name")} value={form.name} onChange={set("name")} onBlur={blur("name")} placeholder="Z-Cars Electric Collection" maxLength={60} aria-invalid={!!showErr("name")} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field id="bc-symbol" label="Token symbol" hint="Shown in wallets. Permanent." error={showErr("symbol")}>
                  <input id="bc-symbol" className={inputCls("symbol") + " font-mono uppercase"} value={form.symbol} onChange={set("symbol")} onBlur={blur("symbol")} placeholder="ZCEV" maxLength={10} aria-invalid={!!showErr("symbol")} />
                </Field>
                <Field id="bc-category" label="Category" error={showErr("category")}>
                  <select id="bc-category" className={inputCls("category")} value={form.category} onChange={set("category")}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field id="bc-price" label="Price per token (USD)" error={showErr("pricePerTokenUsd")}>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[var(--zc-text-3)]">$</span>
                    <input id="bc-price" inputMode="decimal" className={inputCls("pricePerTokenUsd") + " pl-7 tabular-nums"} value={form.pricePerTokenUsd} onChange={set("pricePerTokenUsd")} onBlur={blur("pricePerTokenUsd")} placeholder="150.00" aria-invalid={!!showErr("pricePerTokenUsd")} />
                  </div>
                </Field>
                <Field id="bc-supply" label="Initial supply" hint="You can mint more later." error={showErr("initialSupply")}>
                  <input id="bc-supply" inputMode="numeric" className={inputCls("initialSupply") + " tabular-nums"} value={form.initialSupply} onChange={set("initialSupply")} onBlur={blur("initialSupply")} aria-invalid={!!showErr("initialSupply")} />
                </Field>
              </div>
              <Field id="bc-desc" label="Description (optional)" hint={`${form.description.length}/500 · Shown to investors.`} error={showErr("description")}>
                <textarea id="bc-desc" rows={3} className={inputCls("description") + " h-auto py-2"} value={form.description} onChange={set("description")} onBlur={blur("description")} placeholder="Fractional ownership of…" />
              </Field>
              <Field id="bc-image" label="Cover image" hint="PNG, JPEG or WEBP · up to 5 MB." error={showErr("image")}>
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); pickImage(e.dataTransfer.files?.[0]); }}
                  className={"rounded-xl border-2 border-dashed p-4 flex items-center gap-3 transition-colors "
                    + (dragOver ? "border-[var(--zc-secondary)] bg-[var(--zc-secondary-soft)]" : showErr("image") ? "border-[var(--zc-danger)]" : "border-[var(--zc-border-2)]")}
                >
                  {image ? (
                    <>
                      <img src={imageUrl} alt="" className="w-12 h-12 rounded-lg object-cover" />
                      <div className="flex-1 min-w-0 text-xs">
                        <div className="font-semibold truncate">{image.name}</div>
                        <div className="text-[var(--zc-text-3)]">{(image.size / 1024 / 1024).toFixed(2)} MB</div>
                      </div>
                      <button type="button" onClick={() => setImage(null)} className="p-1.5 rounded-md hover:bg-[var(--zc-bg-3)]" aria-label="Remove image"><X size={14} /></button>
                    </>
                  ) : (
                    <>
                      <ImagePlus size={18} className="text-[var(--zc-text-3)]" />
                      <div className="flex-1 text-xs text-[var(--zc-text-2)]">Drag an image here, or</div>
                      <button type="button" onClick={() => fileRef.current?.click()} className="zc-btn-ghost px-3 h-8 text-xs font-semibold">Browse</button>
                    </>
                  )}
                  <input id="bc-image" ref={fileRef} type="file" accept={IMAGE_TYPES.join(",")} className="sr-only" onChange={(e) => pickImage(e.target.files?.[0])} />
                </div>
              </Field>
            </div>
            <div>
              <div className="eyebrow mb-2">Investor preview</div>
              <PreviewCard form={form} imageUrl={imageUrl} />
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-[1fr,240px] gap-6">
            <dl className="zc-card p-4 text-sm space-y-2.5">
              {[
                ["Name", form.name],
                ["Symbol", <span className="font-mono">{form.symbol}</span>],
                ["Category", CATEGORY_LABEL[form.category]],
                ["Price per token", fmtUsd(Math.round(Number(form.pricePerTokenUsd) * 100))],
                ["Tokens to mint", fmtNum(Number(form.initialSupply))],
                ["Total value at list price", fmtUsd(Math.round(Number(form.pricePerTokenUsd) * 100) * Number(form.initialSupply))],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3">
                  <dt className="text-[var(--zc-text-3)] text-xs">{k}</dt>
                  <dd className="font-semibold text-right tabular-nums">{v}</dd>
                </div>
              ))}
              <div className="pt-3 mt-1 border-t border-[var(--zc-border)] flex gap-2 text-xs text-[var(--zc-text-2)]">
                <ShieldCheck size={14} className="text-[var(--zc-secondary)] shrink-0 mt-0.5" />
                Tokens are minted to the admin wallet and listed for sale immediately.
              </div>
            </dl>
            <PreviewCard form={form} imageUrl={imageUrl} />
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {step === "edit" ? (
            <>
              <button type="button" className="zc-btn-ghost px-4 h-10 text-sm" onClick={() => onOpenChange(false)}>Cancel</button>
              <button type="button" className="zc-btn-secondary px-5 h-10 text-sm font-semibold" onClick={review} data-testid="bc-create-review">
                Review
              </button>
            </>
          ) : (
            <>
              <button type="button" className="zc-btn-ghost px-4 h-10 text-sm inline-flex items-center gap-1.5" onClick={() => setStep("edit")} disabled={busy}>
                <ArrowLeft size={14} /> Edit
              </button>
              <button type="button" className="zc-btn-primary px-5 h-10 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60" onClick={submit} disabled={busy} data-testid="bc-create-submit">
                {busy && <Loader2 size={14} className="animate-spin" />}
                {busy ? "Minting on-chain…" : `Mint ${fmtNum(Number(form.initialSupply))} tokens`}
              </button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
